// Sign-in: the main way is a link sent in WhatsApp (the phone is the
// identity). The email code is the fallback, behind Turnstile.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import { BrandMark, useBrand, whatsappLink } from '@cover/core/brand.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Card, CardBody } from '@cover/core/ui/Card.tsx';
import { Field, Input } from '@cover/core/ui/Form.tsx';
import { useSessionConfig } from '../session.tsx';

declare global {
  interface Window {
    turnstile?: { render: (el: HTMLElement, opts: Record<string, unknown>) => string; reset: (id?: string) => void };
  }
}

function Turnstile({ siteKey, onToken }: { siteKey: string; onToken: (t: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let widget: string | undefined;
    const render = () => {
      if (ref.current && window.turnstile && !widget)
        widget = window.turnstile.render(ref.current, { sitekey: siteKey, callback: onToken, 'expired-callback': () => onToken(null), appearance: 'interaction-only' });
    };
    if (window.turnstile) render();
    else {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.onload = render;
      document.head.appendChild(s);
    }
  }, [siteKey, onToken]);
  return <div ref={ref} />;
}

async function post(path: string, body: unknown) {
  const res = await fetch(path, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const out = (await res.json().catch(() => ({}))) as { redirect?: string; devCode?: string; error?: { message: string } };
  if (!res.ok) throw new Error(out.error?.message ?? 'Something went wrong');
  return out;
}

export function LoginPage() {
  const brand = useBrand();
  const config = useSessionConfig();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const next = params.get('next')?.startsWith('/') ? params.get('next')! : '/';
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [turnstile, setTurnstile] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wa = whatsappLink(brand, 'Can you send me a link to my dashboard?');

  useEffect(() => {
    document.title = `Sign in · ${brand.name}`;
  }, [brand.name]);

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!turnstile) return setError('Please wait a moment while we check this browser, then try again.');
    setBusy(true);
    try {
      const res = (await post('/session/email-code', { email, turnstileToken: turnstile })) as { devCode?: string };
      // Only a local engine or the mock (including the workers.dev demo) returns the code.
      setDevCode(res.devCode ?? null);
      setStep('code');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await post('/session/email-verify', { email, code });
      await qc.invalidateQueries();
      navigate(next, { replace: true });
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto max-w-md px-4 py-10">
      <p className="mb-3">
        <BrandMark />
      </p>
      <h1 className="mb-2 text-2xl font-bold">Sign in to your dashboard</h1>
      <p className="mb-6 text-mid">We send a sign-in link to your WhatsApp. Only your phone gets it, so there’s no password to remember.</p>

      {wa && (
        <Card className="mb-6">
          <CardBody className="space-y-3">
            <Button asChild size="full">
              <a href={wa} className="text-on-brand no-underline hover:no-underline">
                <MessageCircle className="h-5 w-5" aria-hidden /> Get a link in WhatsApp
              </a>
            </Button>
            <p className="text-sm text-mid">This opens WhatsApp with a message ready to send. We’ll reply with your link.</p>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody>
          <h2 className="mb-3 font-semibold">No WhatsApp to hand? Use your email</h2>
          {step === 'email' ? (
            <form onSubmit={sendCode} className="space-y-4" noValidate>
              <Field label="Email address" hint="The one you gave us. We’ll email you a 6-digit code." error={error}>
                <Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </Field>
              {config.data && <Turnstile siteKey={config.data.turnstileSiteKey} onToken={setTurnstile} />}
              <Button type="submit" variant="secondary" size="full" busy={busy} disabled={!email}>
                Email me a code
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4" noValidate>
              <p className="text-sm" aria-live="polite">
                If {email} is on file, a code is on its way. It lasts 10 minutes.
              </p>
              {devCode && <p className="rounded bg-warn-bg px-3 py-2 text-sm text-warn-fg">Test code: {devCode}</p>}
              <Field label="6-digit code" error={error}>
                <Input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
              </Field>
              <Button type="submit" size="full" busy={busy} disabled={code.length !== 6}>
                Sign in
              </Button>
              <button type="button" className="text-sm text-brand underline" onClick={() => setStep('email')}>
                Use a different email
              </button>
            </form>
          )}
        </CardBody>
      </Card>
      {brand.contact.web && (
        <p className="mt-6 text-center text-sm text-mid">
          New to {brand.name}? <a href={`${brand.contact.web.replace(/\/$/, '')}/start`}>Create an account</a>
        </p>
      )}
      {brand.regulatoryLine && <p className="mt-8 text-center text-xs text-muted">{brand.regulatoryLine}</p>}
    </main>
  );
}
